import { useState, useEffect, useCallback } from 'react';
import { getAuth } from 'firebase/auth';

interface ErrorEntry {
  id: string;
  timestamp: string;
  type: 'js' | 'network' | 'react' | 'api' | 'console';
  severity: 'error' | 'warning' | 'critical';
  message: string;
  stack?: string;
  url?: string;
  status?: number;
  source?: string;
  count: number;
}

// Global error store
const errorStore: ErrorEntry[] = [];
let errorListeners: Array<() => void> = [];

function notifyListeners() {
  errorListeners.forEach(fn => fn());
}

function addError(error: Omit<ErrorEntry, 'id' | 'count'>) {
  // Check for duplicate
  const existing = errorStore.find(
    e => e.message === error.message && e.type === error.type
  );
  
  if (existing) {
    existing.count++;
    existing.timestamp = error.timestamp;
  } else {
    errorStore.unshift({
      ...error,
      id: `err-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      count: 1,
    });
  }
  
  // Keep only last 200 errors
  if (errorStore.length > 200) {
    errorStore.pop();
  }
  
  notifyListeners();
}

// Install global error handlers
if (typeof window !== 'undefined') {
  // JavaScript errors
  window.addEventListener('error', (event) => {
    addError({
      timestamp: new Date().toISOString(),
      type: 'js',
      severity: 'error',
      message: event.message || 'Unknown error',
      stack: event.error?.stack,
      source: `${event.filename}:${event.lineno}:${event.colno}`,
    });
  });
  
  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    addError({
      timestamp: new Date().toISOString(),
      type: 'js',
      severity: 'error',
      message: event.reason?.message || String(event.reason) || 'Unhandled Promise Rejection',
      stack: event.reason?.stack,
    });
  });
  
  // Intercept console.error
  const originalConsoleError = console.error;
  console.error = (...args) => {
    addError({
      timestamp: new Date().toISOString(),
      type: 'console',
      severity: 'error',
      message: args.map(a => typeof a === 'object' ? JSON.stringify(a) : String(a)).join(' '),
    });
    originalConsoleError.apply(console, args);
  };
  
  // Intercept console.warn
  const originalConsoleWarn = console.warn;
  console.warn = (...args) => {
    addError({
      timestamp: new Date().toISOString(),
      type: 'console',
      severity: 'warning',
      message: args.map(a => typeof a === 'object' ? JSON.stringify(a) : String(a)).join(' '),
    });
    originalConsoleWarn.apply(console, args);
  };
  
  // Intercept fetch for network errors
  const originalFetch = window.fetch;
  window.fetch = async (...args) => {
    try {
      const response = await originalFetch(...args);
      if (!response.ok && response.status >= 400) {
        let url = 'unknown';
        if (typeof args[0] === 'string') {
          url = args[0];
        } else if (args[0] instanceof Request) {
          url = args[0].url;
        } else if (args[0] instanceof URL) {
          url = args[0].toString();
        }
        addError({
          timestamp: new Date().toISOString(),
          type: 'network',
          severity: response.status >= 500 ? 'error' : 'warning',
          message: `HTTP ${response.status}: ${response.statusText}`,
          url: url,
          status: response.status,
        });
      }
      return response;
    } catch (err) {
      let url = 'unknown';
      if (typeof args[0] === 'string') {
        url = args[0];
      } else if (args[0] instanceof Request) {
        url = args[0].url;
      } else if (args[0] instanceof URL) {
        url = args[0].toString();
      }
      addError({
        timestamp: new Date().toISOString(),
        type: 'network',
        severity: 'error',
        message: err instanceof Error ? err.message : 'Network request failed',
        url: url,
      });
      throw err;
    }
  };
}

export const ErrorsPanel = () => {
  const [errors, setErrors] = useState<ErrorEntry[]>([...errorStore]);
  const [selectedError, setSelectedError] = useState<ErrorEntry | null>(null);
  const [filter, setFilter] = useState<'all' | 'error' | 'warning' | 'critical'>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | ErrorEntry['type']>('all');
  const [apiErrors, setApiErrors] = useState<ErrorEntry[]>([]);
  
  // Subscribe to error updates
  useEffect(() => {
    const update = () => setErrors([...errorStore]);
    errorListeners.push(update);
    return () => {
      errorListeners = errorListeners.filter(fn => fn !== update);
    };
  }, []);
  
  // Fetch API errors from backend
  const fetchApiErrors = useCallback(async () => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) return;
      
      const token = await user.getIdToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'https://republic-atlas-api-946143380554.us-central1.run.app';
      const response = await fetch(`${apiUrl}/api/v1/debug/errors?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setApiErrors(data.map((e: any) => ({
          id: `api-${e.timestamp}`,
          timestamp: e.timestamp,
          type: 'api' as const,
          severity: e.level.toLowerCase() as 'error' | 'warning' | 'critical',
          message: e.message,
          source: e.source,
        })));
      }
    } catch {
      // Silently fail - API might not be available
    }
  }, []);
  
  useEffect(() => {
    fetchApiErrors();
    const interval = setInterval(fetchApiErrors, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [fetchApiErrors]);
  
  const allErrors = [...errors, ...apiErrors].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
  
  const filteredErrors = allErrors.filter(e => {
    if (filter !== 'all' && e.severity !== filter) return false;
    if (typeFilter !== 'all' && e.type !== typeFilter) return false;
    return true;
  });
  
  const errorCount = allErrors.filter(e => e.severity === 'error' || e.severity === 'critical').length;
  const warningCount = allErrors.filter(e => e.severity === 'warning').length;
  
  const clearErrors = () => {
    errorStore.length = 0;
    setErrors([]);
  };
  
  const getSeverityColor = (severity: ErrorEntry['severity']) => {
    switch (severity) {
      case 'warning': return 'text-yellow-400 bg-yellow-900/30';
      case 'error': return 'text-red-400 bg-red-900/30';
      case 'critical': return 'text-red-600 bg-red-900/50 font-bold';
    }
  };
  
  const getTypeIcon = (type: ErrorEntry['type']) => {
    switch (type) {
      case 'js': return '⚡';
      case 'network': return '🌐';
      case 'react': return '⚛️';
      case 'api': return '🔌';
      case 'console': return '📝';
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className={`rounded-lg p-3 border ${
        errorCount > 0 
          ? 'bg-gradient-to-r from-red-900/50 to-orange-900/50 border-red-500/30' 
          : 'bg-gradient-to-r from-green-900/50 to-emerald-900/50 border-green-500/30'
      }`}>
        <p className="text-sm">
          <span className={errorCount > 0 ? 'text-red-400' : 'text-green-400'}>
            {errorCount > 0 ? '⚠️ Error Summary:' : '✅ System Status:'}
          </span>{' '}
          <span className="text-gray-200">
            {errorCount > 0 ? (
              <>
                <span className="text-red-400 font-bold">{errorCount} errors</span>
                {warningCount > 0 && <>, <span className="text-yellow-400">{warningCount} warnings</span></>}
              </>
            ) : (
              <>No errors detected. {warningCount > 0 && <span className="text-yellow-400">{warningCount} warnings</span>}</>
            )}
          </span>
        </p>
      </div>
      
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as typeof filter)}
          className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs"
        >
          <option value="all">All Levels</option>
          <option value="critical">Critical</option>
          <option value="error">Error</option>
          <option value="warning">Warning</option>
        </select>
        
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as typeof typeFilter)}
          className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs"
        >
          <option value="all">All Types</option>
          <option value="js">⚡ JavaScript</option>
          <option value="network">🌐 Network</option>
          <option value="console">📝 Console</option>
          <option value="api">🔌 API</option>
          <option value="react">⚛️ React</option>
        </select>
        
        <button
          onClick={fetchApiErrors}
          className="bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded text-xs"
        >
          🔄 Refresh API
        </button>
        
        <button
          onClick={clearErrors}
          className="bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded text-xs"
        >
          🗑️ Clear
        </button>
        
        <span className="text-xs text-gray-500 ml-auto">
          {filteredErrors.length} of {allErrors.length} shown
        </span>
      </div>
      
      {/* Error List */}
      <div className="bg-gray-900 rounded-lg overflow-hidden">
        <div className="max-h-64 overflow-y-auto">
          {filteredErrors.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {allErrors.length === 0 ? '🎉 No errors captured' : 'No errors matching filters'}
            </div>
          ) : (
            filteredErrors.map((error) => (
              <div
                key={error.id}
                onClick={() => setSelectedError(selectedError?.id === error.id ? null : error)}
                className={`flex items-start gap-2 px-3 py-2 border-b border-gray-800 cursor-pointer hover:bg-gray-800/50 ${
                  selectedError?.id === error.id ? 'bg-gray-800' : ''
                } ${error.severity !== 'warning' ? 'bg-red-900/10' : ''}`}
              >
                <span className="text-lg flex-shrink-0">{getTypeIcon(error.type)}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${getSeverityColor(error.severity)}`}>
                      {error.severity.toUpperCase()}
                    </span>
                    {error.count > 1 && (
                      <span className="bg-purple-600/50 text-purple-300 px-1.5 py-0.5 rounded text-[10px]">
                        ×{error.count}
                      </span>
                    )}
                    <span className="text-[10px] text-gray-600">
                      {new Date(error.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  <p className="text-xs text-gray-300 truncate mt-0.5">{error.message}</p>
                  {error.url && (
                    <p className="text-[10px] text-gray-500 truncate">{error.url}</p>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      
      {/* Selected Error Details */}
      {selectedError && (
        <div className="bg-gray-800 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-red-400">Error Details</h4>
            <button
              onClick={() => setSelectedError(null)}
              className="text-gray-500 hover:text-white text-xs"
            >
              ✕
            </button>
          </div>
          
          <div className="grid grid-cols-2 gap-2 text-xs mb-2">
            <div className="bg-gray-900 rounded p-2">
              <span className="text-gray-500">Type:</span>
              <span className="ml-2 text-gray-300">{getTypeIcon(selectedError.type)} {selectedError.type}</span>
            </div>
            <div className="bg-gray-900 rounded p-2">
              <span className="text-gray-500">Severity:</span>
              <span className={`ml-2 ${getSeverityColor(selectedError.severity)}`}>
                {selectedError.severity}
              </span>
            </div>
            <div className="bg-gray-900 rounded p-2">
              <span className="text-gray-500">Time:</span>
              <span className="ml-2 text-gray-300">
                {new Date(selectedError.timestamp).toLocaleString()}
              </span>
            </div>
            <div className="bg-gray-900 rounded p-2">
              <span className="text-gray-500">Count:</span>
              <span className="ml-2 text-gray-300">{selectedError.count}</span>
            </div>
          </div>
          
          <div className="bg-gray-900 rounded p-2 mb-2">
            <span className="text-gray-500 text-xs">Message:</span>
            <p className="text-red-300 text-sm mt-1 break-all">{selectedError.message}</p>
          </div>
          
          {selectedError.url && (
            <div className="bg-gray-900 rounded p-2 mb-2">
              <span className="text-gray-500 text-xs">URL:</span>
              <p className="text-blue-400 text-xs mt-1 break-all font-mono">{selectedError.url}</p>
            </div>
          )}
          
          {selectedError.source && (
            <div className="bg-gray-900 rounded p-2 mb-2">
              <span className="text-gray-500 text-xs">Source:</span>
              <p className="text-gray-400 text-xs mt-1 font-mono">{selectedError.source}</p>
            </div>
          )}
          
          {selectedError.stack && (
            <div className="bg-gray-900 rounded p-2">
              <span className="text-gray-500 text-xs">Stack Trace:</span>
              <pre className="text-[10px] text-gray-400 mt-1 overflow-x-auto whitespace-pre-wrap max-h-32 overflow-y-auto">
                {selectedError.stack}
              </pre>
            </div>
          )}
        </div>
      )}
      
      {/* Quick Actions */}
      <div className="flex gap-2 text-xs">
        <a
          href="https://console.cloud.google.com/errors?project=republic-atlas"
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 text-center bg-gray-700 hover:bg-gray-600 py-2 rounded text-blue-400"
        >
          🐛 Error Reporting
        </a>
        <a
          href="https://console.firebase.google.com/project/republic-atlas/crashlytics"
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 text-center bg-gray-700 hover:bg-gray-600 py-2 rounded text-orange-400"
        >
          🔥 Crashlytics
        </a>
      </div>
    </div>
  );
};

export default ErrorsPanel;

